<template>
    <v-dialog v-model="dialog" max-width="700">
        <template v-slot:activator="{ props }">
            <div class="d-flex justify-center align-center">
                <v-btn variant="text" :loading="loading" :disabled="loading" v-bind="props" color="accent">
                    <v-icon start>mdi-sticker-check-outline</v-icon>
                    {{ t("kit_mark") }}
                    <span v-if="actuals.length" class="ml-1">({{ actuals.length }})</span>
                </v-btn>
            </div>
        </template>
        <v-card>
            <v-card-title class="text-h5">
                {{ t("set_constructible_as_kitted_parts") }}
            </v-card-title>
            <v-card-text>
                {{ t("set_constructible_as_kitted_parts_text") }}
                <!-- Design names, not uuids. This dialog used to iterate the
                     prop directly, which is an array of uuid strings coming from
                     the treeview's item-value, so the manager confirmed a list
                     of 8-character hex. -->
                <ul class="mt-3">
                    <li v-for="row in rows" :key="row.uuid">
                        {{ row.name }}
                        <span class="text-caption text-medium-emphasis ml-1">{{ row.kit }}</span>
                    </li>
                </ul>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="accent" variant="flat" @click="mark()" :loading="loading" :disabled="loading">
                    {{ t("kit_mark") }}
                </v-btn>
                <v-btn color="secondary" variant="text" @click="dialog = false">
                    {{ t("close") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { usePitStoreBasics } from "@/stores/app_common";
import { backend_api_v2 } from "/src/utils/apiv2";
import { mapActions } from "pinia";
import { useConstructiblesStore } from "@/stores/constructibles";
import { kitTotal, kitReady } from "/src/utils/kits.js";
import bus from "/src/utils/event_bus";

export default {
    setup() { return { ...usePitStoreBasics(), } },
    // `actuals` is a list of constructible uuids, as the treeview hands them over.
    props: ["actuals"],
    data: () => ({
        loading: false,
        dialog: false,
    }),
    computed: {
        /** The selected constructibles, resolved to what a person recognises. */
        rows() {
            const store = useConstructiblesStore();
            return (this.actuals || []).map(uuid => {
                const actual = store.allActuals.find(a => a.uuid == uuid);
                const name = actual
                    ? store.designNameByUuid(actual.design, false)
                    : uuid.substring(0, 8);
                const total = kitTotal(actual);
                return {
                    uuid,
                    name: name || uuid.substring(0, 8),
                    kit: total ? `${kitReady(actual)}/${total}` : "",
                    level: actual?.level || (actual?.module !== undefined ? "assembly" : "module"),
                };
            });
        },
        /** Split by level: the two routes are scoped to their own model, so a
         *  module posted to the assemblies route is silently dropped. */
        by_level() {
            const groups = { assembly: [], module: [] };
            const store = useConstructiblesStore();
            this.rows.forEach(row => {
                const is_module = store.modules?.some(m => m.uuid == row.uuid);
                groups[is_module ? "module" : "assembly"].push(row.uuid);
            });
            return groups;
        },
    },
    methods: {
        ...mapActions(useConstructiblesStore, ["updateAssembly", "updateModule"]),
        async mark() {
            this.loading = true;
            try {
                await Promise.all(Object.entries(this.by_level)
                    .filter(([, uuids]) => uuids.length)
                    .map(([level, uuids]) => this.post(level, uuids)));
                bus.emit("notification", { text: this.t("kit_marked_ok"), color: "success" });
            } catch (error) {
                console.error("Could not mark the kit", error);
                bus.emit("notification", { text: this.t("kit_mark_failed"), color: "error" });
            } finally {
                this.loading = false;
                this.dialog = false;
            }
        },
        post(level, uuids) {
            const url = `/constructibles/${level == "module" ? "modules" : "assemblies"}/mark_kitted/`;
            return backend_api_v2.post(url, { uuids, kitted: true })
                .then(({ data }) => {
                    const results = data?.results || data || [];
                    results.forEach(actual => level == "module"
                        ? this.updateModule(actual)
                        : this.updateAssembly(actual));
                });
        },
    }
}
</script>
